import React, { useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button, Input, Badge } from '@h2/ui';
import { Search, X, Check } from 'lucide-react';
import { useWorkPresetStore, WorkPreset } from '../../stores/useWorkPresetStore';
import { useConfigurationStore } from '../../stores/useConfigurationStore';

interface PresetSelectorProps {
  onPresetSelected?: (preset: WorkPreset) => void;
  expectedSourceLanguage?: string; // 예상되는 원본언어 (configuration에서 전달)
  expectedTargetLanguages?: string[]; // 예상되는 번역언어들 (configuration에서 전달)
}

interface PresetItemProps {
  preset: WorkPreset;
  onSelect: (preset: WorkPreset) => void;
}

const PresetItem: React.FC<PresetItemProps> = ({ preset, onSelect }) => {
  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => onSelect(preset)}>
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h4 className="font-medium text-sm mb-1">{preset.name}</h4>
            <p className="text-xs text-muted-foreground mb-2">{preset.description}</p>
            <div className="flex gap-1 flex-wrap">
              {preset.tags.map(tag => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
          <Button size="sm" className="ml-2">
            <Check className="h-3 w-3 mr-1" />
            선택
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const SelectedPresetDisplay: React.FC<{ preset: WorkPreset; onClear: () => void }> = ({ 
  preset, 
  onClear 
}) => {
  return (
    <Card className="bg-blue-50 border-blue-200">
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Check className="h-4 w-4 text-green-600" />
              <h4 className="font-medium text-sm">선택된 프리셋: {preset.name}</h4>
            </div>
            <p className="text-xs text-muted-foreground mb-2">{preset.description}</p>
            <div className="flex gap-1 flex-wrap">
              {preset.tags.map(tag => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClear}
            className="ml-2 h-8 w-8 p-0 hover:bg-red-100"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const PresetSelector: React.FC<PresetSelectorProps> = ({ 
  onPresetSelected, 
  expectedSourceLanguage, 
  expectedTargetLanguages 
}) => {
  const {
    presets,
    isLoading,
    searchQuery,
    filteredPresets,
    selectedPreset,
    loadPresets,
    setSearchQuery,
    selectPreset,
    clearSelection
  } = useWorkPresetStore();

  const { updateField, formData } = useConfigurationStore();

  // 현재 설정된 원본언어 가져오기 (expectedSourceLanguage 우선, 그 다음 formData)
  const currentSourceLanguage = expectedSourceLanguage || formData.languageSettingsComponent?.sourceLanguage || '';


  // 언어 코드를 한글 이름으로 변환
  const getLanguageName = (code: string) => {
    const languageMap: { [key: string]: string } = {
      'ko': '한국어',
      'en': '영어', 
      'ja': '일본어',
      'zh': '중국어',
      'es': '스페인어',
      'hi': '힌디어'
    };
    return languageMap[code] || code;
  };

  // 한글 이름을 언어 코드로 변환
  const getLanguageCode = (name: string) => {
    const nameToCodeMap: { [key: string]: string } = {
      '한국어': 'ko',
      '영어': 'en',
      '일본어': 'ja',
      '중국어': 'zh',
      '스페인어': 'es',
      '힌디어': 'hi'
    };
    return nameToCodeMap[name] || name;
  };

  // 프리셋 선택 시 언어 호환성 확인 (이제 프리셋에는 언어 설정이 없음)
  const checkLanguageCompatibility = (preset: WorkPreset, expectedSourceLang?: string, expectedTargetLangs?: string[]) => {
    // 프리셋에는 더 이상 언어 설정이 없으므로, 등록 작업이 있으면 언어 설정을 건너뛰고 워크플로로 이동
    // 등록 작업이 없으면 언어 설정으로 이동
    console.log('언어 호환성 검사 (프리셋에는 언어 설정 없음):');
    console.log('- 등록 작업 원본언어:', expectedSourceLang);
    console.log('- 등록 작업 번역언어들:', expectedTargetLangs);
    
    // 등록 작업이 있으면 워크플로로 바로 이동 (언어는 이미 설정됨)
    const hasPreSelectedJobs = expectedSourceLang && expectedTargetLangs && expectedTargetLangs.length > 0;
    
    console.log('- 등록 작업 있음? ', hasPreSelectedJobs);
    return hasPreSelectedJobs; // 등록 작업이 있으면 true (워크플로로 이동)
  };

  // 프리셋에 언어 설정이 없으므로 필터링 제거
  const languageFilteredPresets = useMemo(() => {
    return filteredPresets; // 모든 프리셋 표시
  }, [filteredPresets]);

  useEffect(() => {
    if (presets.length === 0) {
      loadPresets();
    }
  }, [presets.length, loadPresets]);

  const handlePresetSelect = (preset: WorkPreset) => {
    selectPreset(preset);
    
    console.log('프리셋 선택:', preset.name);
    console.log('프리셋 데이터:', preset.configData);
    
    // Configuration 데이터 업데이트 (배치로 처리)
    const { setFormData } = useConfigurationStore.getState();
    
    console.log('🔄 프리셋 데이터를 배치로 적용');
    
    // 현재 formData 가져오기
    const currentFormData = useConfigurationStore.getState().formData;
    
    // 새로운 formData 생성 (프리셋 데이터 병합)
    // 등록 작업이 있으면 언어 설정은 건드리지 않음
    const newFormData = {
      ...currentFormData,
      ...preset.configData
    };
    
    // 등록 작업이 있으면 언어 설정 보존
    if (expectedSourceLanguage && expectedTargetLanguages && expectedTargetLanguages.length > 0) {
      console.log('🔒 등록 작업 언어 설정 보존');
      newFormData.languageSettingsComponent = {
        ...newFormData.languageSettingsComponent,
        sourceLanguage: currentFormData.languageSettingsComponent?.sourceLanguage || expectedSourceLanguage,
        targetLanguages: currentFormData.languageSettingsComponent?.targetLanguages || expectedTargetLanguages.map(lang => {
          const langToCodeMap: { [key: string]: string } = {
            '한국어': 'ko', '영어': 'en', '일본어': 'ja',
            '중국어': 'zh', '스페인어': 'es', '힌디어': 'hi'
          };
          return langToCodeMap[lang] || lang;
        })
      };
    }
    
    console.log('📋 병합된 formData:', newFormData);
    
    // 한 번에 모든 데이터 업데이트
    setFormData(newFormData);

    // 업데이트 후 전체 formData 확인 및 validation 새로고침
    setTimeout(() => {
      const { formData, validateAll, setCurrentStep, markStepCompleted } = useConfigurationStore.getState();
      console.log('📋 프리셋 적용 후 전체 formData:', formData);
      
      // 모든 필드 validation 새로고침
      console.log('🔄 프리셋 적용 후 validation 새로고침');
      validateAll();
      
      // 프리셋 선택 단계 완료
      markStepCompleted(0);
      
      // 번역언어 검사: 프리셋의 번역언어가 모든 작업의 번역언어를 포함하는지 확인
      const shouldSkipToFinal = checkLanguageCompatibility(preset, expectedSourceLanguage, expectedTargetLanguages);
      
      if (shouldSkipToFinal) {
        console.log('🚀 조건 맞음 - 최종 확인으로 이동 (Step 6)');
        // 중간 단계들 완료 처리
        markStepCompleted(1); // 프리셋 선택
        markStepCompleted(2); // 언어 설정 (작업에서 이미 설정됨)
        markStepCompleted(3); // 워크플로 구성 (프리셋에서 적용됨)
        markStepCompleted(4); // 작업자 배정 (프리셋에서 적용됨)
        setCurrentStep(5); // 최종 확인 단계 (6번째 = index 5)
      } else {
        console.log('⚠️ 조건 안맞음 - 작업자 배정으로 이동 (Step 5)');
        markStepCompleted(1); // 프리셋 선택
        markStepCompleted(2); // 언어 설정
        markStepCompleted(3); // 워크플로 구성 (프리셋에서 적용됨)
        setCurrentStep(4); // 작업자 배정 단계 (5번째 = index 4)
      }
    }, 100);

    onPresetSelected?.(preset);
  };

  const handleClearSelection = () => {
    clearSelection();
    
    // Configuration 데이터 초기화 (선택 필드만)
    updateField('selectedPresetId', 'manual');
  };

  if (selectedPreset) {
    return (
      <div className="space-y-4">
        <SelectedPresetDisplay 
          preset={selectedPreset} 
          onClear={handleClearSelection}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 검색 입력 */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder="프리셋 이름, 설명, 태그로 검색..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* 프리셋 리스트 */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            프리셋을 불러오는 중...
          </div>
        ) : languageFilteredPresets.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {currentSourceLanguage ? 
              `${getLanguageName(currentSourceLanguage)} 원본언어에 맞는 프리셋이 없습니다.` :
              (searchQuery ? '검색 결과가 없습니다.' : '사용 가능한 프리셋이 없습니다.')
            }
          </div>
        ) : (
          languageFilteredPresets.map((preset) => (
            <PresetItem
              key={preset.id}
              preset={preset}
              onSelect={handlePresetSelect}
            />
          ))
        )}
      </div>

      {/* 직접 설정 버튼 */}
      <div className="pt-4 border-t">
        <Button
          variant="outline"
          className="w-full"
          onClick={() => {
            console.log('🔧 직접 설정 선택');
            const { setCurrentStep, markStepCompleted } = useConfigurationStore.getState();
            
            // 프리셋 선택 단계만 완료 처리
            markStepCompleted(0);
            markStepCompleted(1); // 작업 유형 선택도 완료
            
            // 언어 설정 단계로 이동 (3번째 단계 = index 2)
            setCurrentStep(2);
          }}
        >
          직접 설정하기
        </Button>
      </div>

      {/* 통계 정보 */}
      {!isLoading && languageFilteredPresets.length > 0 && (
        <div className="text-xs text-muted-foreground text-center mt-2">
          {searchQuery ? 
            `${languageFilteredPresets.length}개의 프리셋을 찾았습니다.` : 
            `총 ${presets.length}개의 프리셋이 있습니다.`
          }
        </div>
      )}
    </div>
  );
};
